/**
 * RegisterForm コンポーネント用スタイル定義
 */

// カラー定義
const COLORS = {
  primary: '#007bff',
  success: '#28a745',
  danger: '#dc3545',
  warning: '#ffc107',
  info: '#17a2b8',
  light: '#f8f9fa',
  dark: '#343a40',
  white: '#fff',
  gray: {
    100: '#f8f9fa',
    200: '#e9ecef',
    300: '#dee2e6',
    400: '#ced4da',
    500: '#adb5bd',
    600: '#6c757d',
    700: '#495057',
    800: '#343a40',
    900: '#212529'
  },
  border: '#ddd',
  text: '#333',
  textMuted: '#666',
  required: 'red',
  readOnlyBg: '#f8f9fa',
  readOnlyText: '#333'
};

// 基本スタイル
export const registerFormStyles = {
  container: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
    lineHeight: '1.6'
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '20px'
  },

  th: {
    border: `1px solid ${COLORS.border}`,
    padding: '12px',
    textAlign: 'left',
    backgroundColor: '#f8f9fa',  // 薄い灰色背景
    fontWeight: 'bold',
    width: '200px',
    verticalAlign: 'top'
  },

  td: {
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: COLORS.border,
    padding: '12px',
    textAlign: 'left',
    backgroundColor: '#ffffff',
    borderRadius: '4px'
  },

  // 入力フィールド共通スタイル
  input: {
    width: '100%',
    padding: '8px 12px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: COLORS.gray[300],
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box',
    backgroundColor: '#ffffff', 
    color: '#333333',
    outline: 'none',
    opacity: 1,
    WebkitAppearance: 'none',
    appearance: 'none',
    WebkitBoxShadow: '0 0 0 1000px #ffffff inset',
    transition: 'border-color 0.2s ease'
  },

  select: {
    width: '100%',
    padding: '8px 12px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: COLORS.gray[300],
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
    color: '#333333',
    outline: 'none',
    opacity: 1,
    WebkitAppearance: 'none',
    appearance: 'none',
    transition: 'border-color 0.2s ease'
  },

  // 郵便番号検索用コンテナ
  postalContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap'
  },

  postalInput: {
    flex: '0 0 200px',
    minWidth: '200px',
    maxWidth: '250px',
    boxSizing: 'border-box'
  },

  postalButton: {
    backgroundColor: COLORS.primary,
    color: COLORS.white,
    borderWidth: '0',
    borderStyle: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    fontSize: '14px',
    fontWeight: 'bold',
    transition: 'background-color 0.2s ease',
    ':hover': {
      backgroundColor: '#0056b3'
    }
  },

  // ラジオボタン用スタイル
  radioGroup: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap'
  },

  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer'
  },

  radioInput: {
    marginRight: '5px'
  },

  // ボタンスタイル
  submitButton: {
    backgroundColor: COLORS.success,
    color: COLORS.white,
    borderWidth: '0',
    borderStyle: 'none',
    padding: '12px 30px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'background-color 0.2s ease',
    ':hover': {
      backgroundColor: '#218838'
    }
  },

  // テキストスタイル
  required: {
    color: COLORS.required
  },

  smallText: {
    fontSize: '85%',
    color: COLORS.textMuted
  },

  // メッセージスタイル
  message: {
    marginTop: '10px',
    fontSize: '14px'
  },

  error: {
    color: COLORS.danger
  },

  success: {
    color: COLORS.success
  },

  // 読み取り専用フィールド
  readOnly: {
    backgroundColor: '#f8f9fa',  
    color: COLORS.readOnlyText,
    cursor: 'not-allowed',
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: COLORS.gray[300],
    opacity: 0.8
  },

  // レイアウト
  centerAlign: {
    textAlign: 'center'
  },

  pageTitle: {
    textAlign: 'center',
    marginBottom: '30px',
    color: COLORS.text,
    fontSize: '24px',
    fontWeight: 'bold'
  },

  navigationInfo: {
    backgroundColor: '#d1ecf1',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#bee5eb',
    padding: '15px',
    borderRadius: '4px',
    marginBottom: '20px',
    textAlign: 'center'
  },

  navigationText: {
    color: '#0c5460',
    margin: 0,
    fontSize: '14px'
  }
};

/**
 * 動的スタイルを生成する関数
 */
export const createDynamicStyles = {
  // 入力フィールドの状態に応じたスタイル
  getInputStyle: (hasError = false, isReadOnly = false) => ({
    ...registerFormStyles.input,
    ...(hasError && { 
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: COLORS.danger,
      boxShadow: `0 0 0 0.2rem rgba(220, 53, 69, 0.25)`
    }),
    ...(isReadOnly && registerFormStyles.readOnly)
  }),

  // セレクトボックスの状態に応じたスタイル
  getSelectStyle: (hasError = false) => ({
    ...registerFormStyles.select,
    ...(hasError && { 
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: COLORS.danger,
      boxShadow: `0 0 0 0.2rem rgba(220, 53, 69, 0.25)`
    })
  }),

  // ボタンの状態に応じたスタイル
  getButtonStyle: (variant = 'primary', disabled = false) => {
    const baseButton = {
      borderWidth: '0',
      borderStyle: 'none',
      padding: '8px 12px',
      borderRadius: '4px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      transition: 'all 0.2s ease'
    };

    const variants = {
      primary: {
        backgroundColor: COLORS.primary,
        color: COLORS.white
      },
      success: {
        backgroundColor: COLORS.success,
        color: COLORS.white
      },
      danger: {
        backgroundColor: COLORS.danger,
        color: COLORS.white
      }
    };

    return {
      ...baseButton,
      ...variants[variant]
    };
  },

  // メッセージタイプに応じたスタイル
  getMessageStyle: (type = 'info') => {
    const messageTypes = {
      success: {
        ...registerFormStyles.message,
        color: COLORS.success
      },
      error: {
        ...registerFormStyles.message,
        color: COLORS.danger
      },
      info: {
        ...registerFormStyles.message,
        color: COLORS.info
      }
    };

    return messageTypes[type] || messageTypes.info;
  }
};

/**
 * レスポンシブスタイル（メディアクエリ対応）
 */
export const responsiveStyles = {
  mobile: {
    '@media (max-width: 768px)': {
      container: {
        ...registerFormStyles.container,
        padding: '10px'
      },
      postalContainer: {
        ...registerFormStyles.postalContainer,
        flexDirection: 'column',
        alignItems: 'stretch'
      },
      postalInput: {
        flex: '1 1 auto',
        minWidth: 'unset',
        maxWidth: 'unset'
      },
      radioGroup: {
        ...registerFormStyles.radioGroup,
        flexDirection: 'column',
        gap: '10px'
      }
    }
  }
};

/**
 * テーマ対応（ダークモードなど将来の拡張用）
 */
export const themes = {
  light: {
    colors: COLORS,
    styles: registerFormStyles
  },
  // 将来的にダークテーマなどを追加可能
  dark: {
    colors: {
      ...COLORS,
      primary: '#4dabf7',
      text: '#fff',
      border: '#495057',
      light: '#343a40'
    }
  }
};

export default registerFormStyles;